import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { ClipboardList, User, FileText, Globe } from "lucide-react";
import { formatSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

const actionColors = {
  CREATE_USER: "bg-emerald-50 text-emerald-700 border-emerald-200",
  UPDATE_USER: "bg-blue-50 text-blue-700 border-blue-200",
  RESET_PASSWORD: "bg-amber-50 text-amber-700 border-amber-200",
  APPROVE_LEAVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED_LEAVE: "bg-red-50 text-red-700 border-red-200",
  APPROVE_TIMESHEET: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECT_TIMESHEET: "bg-red-50 text-red-700 border-red-200",
  EXPORT_PAYROLL: "bg-purple-50 text-purple-700 border-purple-200",
  EXPORT_PERFORMANCE: "bg-purple-50 text-purple-700 border-purple-200",
};

export default function AdminAudit() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverTime, setServerTime] = useState(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await api().get("/admin/audit-logs");
        setLogs(response.data);
      } catch (error) {
        toast.error("Failed to load audit logs");
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-audit">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Audit Logs</h1>
        <p className="text-slate-500 mt-1">Track administrative actions and changes</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <ClipboardList className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{logs.length}</p>
                <p className="text-sm text-slate-500">Total Actions Logged</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {logs.filter(l => l.action.includes("EXPORT")).length}
                </p>
                <p className="text-sm text-slate-500">Exports Generated</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs List */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p>No audit logs yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {logs.map((log, index) => (
                <div
                  key={log.id}
                  className={`p-4 hover:bg-slate-50 transition-colors animate-fade-in stagger-${Math.min(index + 1, 6)}`}
                  data-testid={`audit-log-${log.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <User className="h-4 w-4 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-medium text-slate-900">{log.user_name}</span>
                        <Badge className={actionColors[log.action] || "bg-slate-50 text-slate-600 border-slate-200"}>
                          {log.action.replace(/_/g, " ")}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{log.details}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {formatSydney(log.created_at, "MMM dd, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
